import React, { FC, useMemo } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { PrinterProfile } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { analyticsService } from '../../analytics/AnalyticsService';
import { useSession } from '../../hooks/useSession';
import { useColors } from '../../hooks/useColors';
import { useSettingsSync } from './SettingsSyncContext';
import { usePrinterProfiles } from '../../hooks/usePrinterProfiles';
import { useDocketFilterSelection } from '../../hooks/useDocketFilterSelection';
import { useDocketFilterOptions } from '../../hooks/useDocketFilterOptions';
import { pruneSelection, toggleSelection } from '../../utils/docketFilter';
import { createNameComparator, sortByName } from '../../utils/sorting';
import SettingsItem from '../../component/SettingsItem/SettingsItem';
import SectionHeading from './SectionHeading';
import DocketFilterRow from './DocketFilterRow';
import SettingsScreenStyles from './Settings.styles';

const RoutingSettings: FC = () => {
  const styles = SettingsScreenStyles();
  const { translate } = useTranslation();
  const { colors } = useColors();
  const { session } = useSession();
  const { loading } = useSettingsSync();
  const { printerProfiles, assignPrinterProfiles } = usePrinterProfiles();
  const { saveSelection, loading: saving } = useDocketFilterSelection();
  const {
    devices,
    sections,
    loading: optionsLoading,
  } = useDocketFilterOptions();

  const count = (selected: number, total: number) =>
    translate('kitchenDisplay.selectedCount', { selected, total });

  const deviceOptions = useMemo(() => devices ?? [], [devices]);
  const sectionOptions = useMemo(() => sections ?? [], [sections]);
  const tickedDeviceIds = useMemo(
    () =>
      pruneSelection(
        session?.kitchenDisplay?.devices,
        deviceOptions.map(d => d.id),
      ) ?? [],
    [session?.kitchenDisplay?.devices, deviceOptions],
  );
  const tickedSectionIds = useMemo(
    () =>
      pruneSelection(
        session?.kitchenDisplay?.sections,
        sectionOptions.map(s => s.id),
      ) ?? [],
    [session?.kitchenDisplay?.sections, sectionOptions],
  );

  const onToggleFilter = (
    field: 'devices' | 'sections',
    ticked: string[],
    id: string,
  ) => {
    if (saving) return;
    saveSelection(field, toggleSelection(ticked, id));
  };

  const assignedProfiles = useMemo(
    () =>
      session?.kitchenDisplay?.printerProfiles?.map(
        (x: PrinterProfile) => x.id,
      ) || [],
    [session?.kitchenDisplay?.printerProfiles],
  );

  // The query returns profiles unordered, so the rows would otherwise shuffle
  // between fetches.
  const sortedProfiles = useMemo(
    () =>
      sortByName(printerProfiles, createNameComparator(session?.localeCode)),
    [printerProfiles, session?.localeCode],
  );

  const onToggleProfile = (printerProfile: PrinterProfile) => {
    analyticsService.capture('Changed Printer Profile');
    const profileId = printerProfile?.id;
    if (!profileId) {
      return;
    }

    const index = assignedProfiles.indexOf(profileId);
    const profilesToAssign = [...assignedProfiles];

    if (index !== -1) {
      if (assignedProfiles.length > 1) {
        profilesToAssign.splice(index, 1);
      } else {
        return;
      }
    } else {
      profilesToAssign.push(profileId);
    }

    if (
      profilesToAssign.length > 0 &&
      JSON.stringify(profilesToAssign) !== JSON.stringify(assignedProfiles)
    ) {
      assignPrinterProfiles(profilesToAssign);
    }
  };

  return (
    <View style={styles.panel} ph-label="settings-routing">
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-printerProfile"
          title={translate('kitchenDisplay.printerProfile')}
        />
        {sortedProfiles.length ? (
          sortedProfiles.map((profile, i) => (
            <SettingsItem
              key={i}
              type="select"
              testID="select-profile"
              loading={loading}
              title={profile.name}
              onChange={() => onToggleProfile(profile)}
              checked={assignedProfiles.includes(profile.id) && !loading}
            />
          ))
        ) : (
          <View>
            <ActivityIndicator color={colors.text} />
          </View>
        )}
      </View>
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-orderFiltering"
          title={translate('kitchenDisplay.orderFiltering')}
        />
        <DocketFilterRow
          testID="select-posDevices"
          optionTestIDPrefix="device"
          title={translate('kitchenDisplay.posDevices')}
          options={deviceOptions}
          selectedIds={tickedDeviceIds}
          loading={loading || optionsLoading}
          labels={{ all: translate('kitchenDisplay.allDevices'), count }}
          onToggle={id => onToggleFilter('devices', tickedDeviceIds, id)}
        />
        <DocketFilterRow
          testID="select-sections"
          optionTestIDPrefix="section"
          title={translate('kitchenDisplay.orderSections')}
          options={sectionOptions}
          selectedIds={tickedSectionIds}
          loading={loading || optionsLoading}
          labels={{ all: translate('kitchenDisplay.allSections'), count }}
          onToggle={id => onToggleFilter('sections', tickedSectionIds, id)}
        />
      </View>
    </View>
  );
};

export default RoutingSettings;
